import {
  Habit,
  HabitSchedule,
  RepeatingTask,
  ScheduleEntryState,
  Timestamp,
} from '@griply/types';
import { endOfDay, isAfter, isBefore, isSameDay } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { getScheduleFrequencyDayTarget } from '../get-schedule-frequency-day-target';
import { isNumber } from '../is-number';
import { getScheduleEndDate } from './get-schedule-end-date';

export type UpdateScheduleEntryOptions = {
  today: Timestamp;
  type?: ScheduleEntryState;
  value?: number;
  increment?: number;
  toggle?: boolean;
};

export const updateScheduleEntryForDate = (
  habit: Habit | RepeatingTask,
  date: Timestamp,
  {
    today,
    type = ScheduleEntryState.Complete,
    value,
    increment,
    toggle,
  }: UpdateScheduleEntryOptions,
): HabitSchedule[] => {
  if (isAfter(date, endOfDay(today))) {
    return habit.schedules;
  }

  // Sort schedules by startDate
  const sortedSchedules = [...habit.schedules].sort(
    (scheduleA, scheduleB) =>
      scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
  );

  // Clone schedules to avoid mutations
  return structuredClone(sortedSchedules).map((scheduleClone, index) => {
    // Get the previous schedule to see if the date falls between this and the previous schedule
    const prevSchedule = sortedSchedules[index - 1];
    // Get the start point for entries to be added to the current schedule
    const startPointSchedule = getScheduleEndDate(prevSchedule) ?? new Date(0);
    // If the date is not within the schedule timeframe, filter the date out of the entries and early return
    if (
      isBefore(date, startPointSchedule) ||
      (scheduleClone.endDate &&
        isAfter(date, getScheduleEndDate(scheduleClone)!))
    ) {
      scheduleClone.entries = scheduleClone.entries.filter(
        (entry) => !isSameDay(entry.date, date),
      );
      // Backwards compatibility
      scheduleClone.completions =
        scheduleClone.completions?.filter(
          (completion) => !isSameDay(completion, date),
        ) ?? null;
      scheduleClone.skips =
        scheduleClone.skips?.filter((skip) => !isSameDay(skip, date)) ?? null;

      return scheduleClone;
    }

    // Get the increment value
    const incrementValue = increment ?? 1;

    // Get the current entry for the given date
    let entry = scheduleClone.entries.find((entry) =>
      isSameDay(entry.date, date),
    );
    // If there is no current entry, add an entry to the list
    if (!entry) {
      entry = {
        id: uuidv4(),
        date,
        value: null,
        state: type,
      };

      scheduleClone.entries = [...scheduleClone.entries, entry];
    }

    // Make sure the state is correct
    entry.state = type;
    // A user action on this day overrides any prior period-skip bookkeeping.
    delete entry.hidden;
    const entryVal = entry.value ?? 0;
    // Check the current value of the entry and change it to the given value, or up it with one if it is a complete
    const newValue = Math.max(
      0,
      value ??
        (isNumber(increment)
          ? // If increment, add the incremental value
            entryVal + incrementValue
          : // If no frequency and no increment, reset to the start if the target has been reached already
            (!scheduleClone.frequency &&
                entryVal >= scheduleClone.completionTarget.count) ||
              // If frequency and day-target is 1, reset to start if the target has been reached already
              (!!scheduleClone.frequency &&
                entryVal &&
                (getScheduleFrequencyDayTarget(scheduleClone) ||
                  entryVal >= scheduleClone.frequency.count)) ||
              (toggle && !!entryVal)
            ? 0
            : entryVal + 1),
    );

    // Set the new value and time (date) on the entry
    entry.value = type === ScheduleEntryState.Complete ? newValue : null;
    entry.date = date;

    // Backwards compatibility
    if (
      // When the frequency is the old settings
      (scheduleClone.frequency &&
        !!getScheduleFrequencyDayTarget(scheduleClone)) ||
      // When the rrule is 1 per day
      scheduleClone.completionTarget.count > 1
    ) {
      let filteredSkips =
        scheduleClone.skips?.filter(
          (skip: Timestamp) => !isSameDay(skip, date),
        ) ?? null;
      let filteredCompletions =
        scheduleClone.completions?.filter(
          (completion: Timestamp) => !isSameDay(completion, date),
        ) ?? null;

      if (
        type === ScheduleEntryState.Skip &&
        (!scheduleClone.skips ||
          filteredSkips?.length !== scheduleClone.skips?.length)
      ) {
        filteredSkips = [...(filteredSkips ?? []), date];
      }

      if (
        type === ScheduleEntryState.Complete &&
        (!scheduleClone.completions ||
          filteredCompletions?.length === scheduleClone.completions?.length)
      ) {
        filteredCompletions = [...(filteredCompletions ?? []), date];
      }
      scheduleClone.skips = filteredSkips;
      scheduleClone.completions = filteredCompletions;
    }

    return scheduleClone;
  });
};
